import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

interface SmsEditorProps {
  name?: string;
  label?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
}

const SMS_CHAR_LIMITS = {
  SINGLE: 160,
  MULTI: 320,
};

// Replace Polish characters with their ASCII equivalents
const replacePolishChars = (text: string): string => {
  const polishCharsMap: Record<string, string> = {
    ą: "a",
    ć: "c",
    ę: "e",
    ł: "l",
    ń: "n",
    ó: "o",
    ś: "s",
    ź: "z",
    ż: "z",
    Ą: "A",
    Ć: "C",
    Ę: "E",
    Ł: "L",
    Ń: "N",
    Ó: "O",
    Ś: "S",
    Ź: "Z",
    Ż: "Z",
  };

  return text.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (char) => polishCharsMap[char] || char);
};

export const SmsEditor = ({
  name = "content",
  label = "Treść SMS *",
  error,
  placeholder = "Wprowadź treść wiadomości SMS...",
  disabled = false,
}: SmsEditorProps) => {
  const { register, watch, setValue } = useFormContext();

  const content = watch(name) || "";
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    setCharCount(content.length);
  }, [content]);

  const getCharCountColor = () => {
    if (charCount <= SMS_CHAR_LIMITS.SINGLE) return "text-green-600";
    if (charCount <= SMS_CHAR_LIMITS.MULTI) return "text-yellow-600";
    return "text-red-600";
  };

  const getMessageInfo = () => {
    if (charCount <= SMS_CHAR_LIMITS.SINGLE) {
      return `1 SMS (${charCount}/${SMS_CHAR_LIMITS.SINGLE} znaków)`;
    }
    if (charCount <= SMS_CHAR_LIMITS.MULTI) {
      return `2 SMS (${charCount}/${SMS_CHAR_LIMITS.MULTI} znaków)`;
    }
    return `Przekroczono limit (${charCount}/${SMS_CHAR_LIMITS.MULTI} znaków)`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const sanitizedValue = replacePolishChars(value);

    // Enforce character limit
    if (sanitizedValue.length <= SMS_CHAR_LIMITS.MULTI) {
      setValue(name, sanitizedValue);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const sanitizedValue = replacePolishChars(value);
    setValue(name, sanitizedValue);
  };

  return (
    <div className="space-y-2">
      <div>{label}</div>

      <div className="relative">
        <textarea
          {...register(name)}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-full rounded-md border px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500 ${error ? "border-red-300" : "border-gray-300"} `}
          rows={6}
          style={{ resize: "vertical" }}
        />

        {/* Character counter overlay */}
        <div
          className={`absolute bottom-2 right-2 rounded border bg-white/90 px-2 py-1 text-xs font-medium shadow-sm backdrop-blur-sm ${getCharCountColor()} `}
        >
          {getMessageInfo()}
        </div>
      </div>
      {/* TODO */}
      {error && <div>{error}</div>}

      {/* SMS information panel */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
        <div className="text-sm text-blue-800">
          <div className="mb-1 font-medium">Informacje o SMS:</div>
          <ul className="list-inside list-disc space-y-1 text-xs">
            <li>Do 160 znaków = 1 SMS</li>
            <li>161-320 znaków = 2 SMS (wyższy koszt)</li>
            <li>Maksymalnie 320 znaków</li>
            <li>Polskie znaki (ą, ć, ę, ł, ń, ó, ś, ź, ż) są automatycznie zamieniane na odpowiedniki łacińskie</li>
            <li>Obsługiwane znaki: litery, cyfry, podstawowa interpunkcja</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
